import { numberToLocaleString } from 'modules/utils/numberFormatUtils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getOverallAmount,
  getPaymentTotalAmountByName,
  getTotalAmountByLeasingType,
  getTranslatedLeasingDetailsName,
  getTranslatedNominalInterestRate,
  getTranslatedParticipation,
  LeasingDetailsKeys,
} from 'modules/leasingOfferOverviewPage/helper';
import { dateToString } from 'modules/utils/mappers/dateMapper';
import {
  InterestRateType,
  LeasingOfferDetails,
  LeasingType,
} from '../../../types/LeasingOffer';

interface PaymentDetails {
  key: string;
  value: string;
  accented?: boolean;
}

export const useOfferOverviewActions = (
  offerOverview?: LeasingOfferDetails,
) => {
  const { t } = useTranslation();

  const getTitleText = useMemo(() => {
    const leasingTypeTitle = t(
      'leasingOfferOverviewPage.offerOverviewTab.leasing',
      { leasingType: offerOverview?.leasingTypeName },
    );

    const leasingOfferDateTitle = dateToString(offerOverview?.creationDate);

    return {
      leasingTypeTitle,
      leasingOfferDateTitle,
    };
  }, [offerOverview?.creationDate, offerOverview?.leasingTypeName, t]);

  const getLeasingObjectDetails = useMemo(() => {
    const commonObjectDetails = [
      {
        key: t('leasingOfferOverviewPage.offerOverviewTab.interestRateType'),
        value: offerOverview?.interestRateType,
      },
      {
        key: getTranslatedNominalInterestRate(offerOverview?.interestRateType),
        value: `${numberToLocaleString(offerOverview?.interestRate, 0, 4)}%`,
      },
      {
        key: t(
          'leasingOfferOverviewPage.offerOverviewTab.leasingPaymentPeriodType',
        ),
        value: offerOverview?.paymentPeriod,
      },
      {
        key: t(
          'leasingOfferOverviewPage.offerOverviewTab.totalNumberOfInstallments',
        ),
        value: offerOverview?.numberOfAnnuities,
      },
    ];

    if (offerOverview?.interestRateType === InterestRateType.VARIABLE) {
      const interestRateTypeIndex = commonObjectDetails.findIndex(
        (element) => element.value === offerOverview?.interestRateType,
      );

      const variableInterestRateDetails = [
        {
          key: t(
            'leasingOfferOverviewPage.offerOverviewTab.referentInterestRate',
          ),
          value: offerOverview?.referentInterestRate,
        },
        {
          key: t(
            'leasingOfferOverviewPage.offerOverviewTab.referentInterestRateValue',
          ),
          value: t(
            'leasingOfferOverviewPage.offerOverviewTab.indexValueInCurrentInterval',
            {
              indexValue: numberToLocaleString(offerOverview?.indexValue, 0, 3),
            },
          ),
        },
        {
          key: t('leasingOfferOverviewPage.offerOverviewTab.margin'),
          value: t('leasingOfferOverviewPage.offerOverviewTab.marginValue', {
            margin: numberToLocaleString(offerOverview?.margin, 0, 3),
          }),
        },
      ];

      commonObjectDetails.splice(
        interestRateTypeIndex + 1,
        0,
        ...variableInterestRateDetails,
      );
    }

    return commonObjectDetails;
  }, [
    offerOverview?.financingDuration,
    offerOverview?.indexValue,
    offerOverview?.interestRate,
    offerOverview?.interestRateType,
    offerOverview?.leasingObjectMake,
    offerOverview?.leasingObjectModel,
    offerOverview?.margin,
    offerOverview?.numberOfAnnuities,
    offerOverview?.paymentPeriod,
    offerOverview?.referentInterestRate,
    offerOverview?.supplier,
    t,
  ]);

  const getOverviewTableColumns = useMemo(() => {
    const commonOverviewTableColumns = [
      {
        label: '',
        accessor: 'name',
      },
      {
        label: '',
        accessor: 'currency',
      },
      {
        label: '',
        accessor: 'percentage',
      },
      {
        label: t(
          'leasingOfferOverviewPage.offerOverviewTab.overviewTable.total',
        ),
        accessor: 'total',
      },
      {
        label: t('leasingOfferOverviewPage.offerOverviewTab.overviewTable.pdv'),
        accessor: 'pdv',
      },
      {
        label: t(
          'leasingOfferOverviewPage.offerOverviewTab.overviewTable.amountWithoutPdv',
        ),
        accessor: 'amountWithoutPdv',
      },
    ];
    return commonOverviewTableColumns;
  }, [offerOverview?.leasingTypeName, t]);

  const getOfferOverviewTableItems = useMemo(
    () =>
      offerOverview?.leasingDetails?.map((leasingObjectDetails) => ({
        name: getTranslatedLeasingDetailsName(
          leasingObjectDetails.name,
          offerOverview?.leasingTypeName,
          offerOverview?.interestRateType,
        ),
        currency: leasingObjectDetails?.currency,
        percentage: leasingObjectDetails?.percentage
          ? `${numberToLocaleString(leasingObjectDetails?.percentage, 2, 2)}%`
          : '',
        total: numberToLocaleString(leasingObjectDetails?.amounts?.total),
        pdv: numberToLocaleString(leasingObjectDetails?.amounts?.taxes),
        amountWithoutPdv: numberToLocaleString(
          leasingObjectDetails?.amounts?.totalWithoutTaxes,
        ),
      })) || [],
    [
      offerOverview?.interestRateType,
      offerOverview?.leasingDetails,
      offerOverview?.leasingTypeName,
    ],
  );

  const getLeasingPaymentDetails = useMemo(() => {
    const commonPaymentDetails: PaymentDetails[] = [
      {
        key: getTranslatedParticipation(offerOverview?.leasingTypeName),
        value: numberToLocaleString(offerOverview?.participationAmount),
      },
      {
        key: t(
          'leasingOfferOverviewPage.offerOverviewTab.leasingAdministrativeExpenses',
        ),
        value: numberToLocaleString(
          getPaymentTotalAmountByName(
            LeasingDetailsKeys.LEASING_ADMINISTRATIVE_EXPENSES,
            offerOverview?.leasingDetails,
          ),
        ),
      },
    ];

    if (offerOverview?.leasingTypeName === LeasingType.OPERATIONAL) {
      const participationIndex = commonPaymentDetails.findIndex(
        (element) =>
          element.key ===
          getTranslatedParticipation(offerOverview?.leasingTypeName),
      );

      commonPaymentDetails.splice(participationIndex + 1, 0, {
        key: t('leasingOfferOverviewPage.offerOverviewTab.bail'),
        value: numberToLocaleString(
          getPaymentTotalAmountByName(
            LeasingDetailsKeys.BAIL,
            offerOverview?.leasingDetails,
          ),
        ),
      });
    }

    commonPaymentDetails.push({
      key: t('leasingOfferOverviewPage.offerOverviewTab.leasingTotalValue'),
      value: numberToLocaleString(getTotalAmountByLeasingType(offerOverview)),
      accented: true,
    });

    return commonPaymentDetails;
  }, [offerOverview, t]);

  const getOverallLeasingPaymentDetails = useMemo(() => {
    if (offerOverview?.leasingTypeName === LeasingType.FINANCIAL) {
      const commonPaymentDetails: PaymentDetails[] = [
        {
          key: t(
            'leasingOfferOverviewPage.offerOverviewTab.overallLeasingTotalValue',
          ),
          value: numberToLocaleString(getOverallAmount(offerOverview)),
          accented: true,
        },
      ];

      return commonPaymentDetails;
    }
    return [];
  }, [offerOverview, t]);

  const getOfferContentData = useMemo(() => {
    const isOperational =
      offerOverview?.leasingTypeName === LeasingType.OPERATIONAL;

    const disclaimerKey = isOperational
      ? 'leasingOfferOverviewPage.offerOverviewTab.disclaimerOperational'
      : 'leasingOfferOverviewPage.offerOverviewTab.disclaimer';

    return [
      {
        key: 'disclaimer',
        value: t(disclaimerKey),
      },
      {
        key: 'offerIsInformative',
        value: t(
          'leasingOfferOverviewPage.offerOverviewTab.offerIsInformative',
        ),
      },
      {
        key: 'allElementsDisclamer',
        value: t(
          'leasingOfferOverviewPage.offerOverviewTab.allElementsDisclamer',
        ),
      },
      {
        key: 'leasingCode',
        value: t('leasingOfferOverviewPage.offerOverviewTab.leasingCode'),
      },
      {
        key: 'fixedLeasing',
        value: t('leasingOfferOverviewPage.offerOverviewTab.fixedLeasing'),
      },
      {
        key: 'informationUnicredit',
        value: t(
          'leasingOfferOverviewPage.offerOverviewTab.informationUnicredit',
        ),
      },
    ];
  }, [offerOverview?.leasingTypeName, t]);

  const getConsultantDetails = useMemo(
    () => [
      {
        key: 'consultant',
        value: t(
          'leasingOfferOverviewPage.offerOverviewTab.unicreditLeasingCroatia',
        ),
        accented: true,
      },
      {
        key: 'consultantPhone',
        value: t(
          'leasingOfferOverviewPage.offerOverviewTab.leasingOfferConsultantPhone',
          { phone: offerOverview?.contactPhone },
        ),
      },
      {
        key: 'consultantEmail',
        value: t(
          'leasingOfferOverviewPage.offerOverviewTab.leasingOfferConsultantEmail',
          { email: offerOverview?.contactEmail },
        ),
      },
    ],
    [offerOverview?.contactEmail, offerOverview?.contactPhone, t],
  );

  return {
    titleText: getTitleText,
    overviewTableColumns: getOverviewTableColumns,
    offerOverviewTableObjects: getOfferOverviewTableItems,
    leasingObjectDetails: getLeasingObjectDetails,
    leasingPaymentDetails: getLeasingPaymentDetails,
    overallLeasingPaymentDetails: getOverallLeasingPaymentDetails,
    offerContentData: getOfferContentData,
    consultantDetails: getConsultantDetails,
  };
};
